import { delayUntil } from '../utils';
import {
    clearStoredAccessToken,
    clearStoredMasqToken,
    clearStoredRefreshToken,
    storeAccessToken,
    storeMasqToken,
    storeRefreshToken,
} from './helpers';
import { watchAuthEvents } from './tokenSync';
import type { UserDetails } from '@thinkalpha/ui-types/types/user';
import axios, { AxiosError } from 'axios';
import { all, call, getContext, put, race, spawn, take, takeLatest } from 'redux-saga/effects';
import { PERSISTED_CURRENT_USER } from 'src/features/local-storage';
import { decodeToken, getExpirationDate } from 'src/features/oauth2';
import { localforage } from 'src/lib/serialization/localForage';
import { container } from 'src/StaticContainer';
import {
    setUserDetails,
    setMasqUserDetails,
    clearMasq,
    clearAccessToken,
    clearRefreshToken,
} from 'src/store/actions/auth';
import type { ClearTokenAction, SetTokenAction } from 'src/store/actions/auth';
import type { SagaContext } from 'src/store/types';

const log = container.get('Logger').getSubLogger({ name: 'auth sagas' }, { component: 'auth' });

export function* setAccessTokenSaga({ type, token, isInitial }: SetTokenAction) {
    log.debug({ message: 'Using access token', token });

    // Validate token has a valid token string - corrupted storage or invalid events can cause this
    const hasValidTokenString = token && typeof token.token === 'string' && token.token.length > 0;

    let userDetailsSetter: typeof setUserDetails;
    if (type === 'access-token::set') {
        if (!isInitial && hasValidTokenString) {
            // Only store valid tokens to prevent corrupting storage
            yield storeAccessToken(token);
        }
        yield localforage.removeItem(PERSISTED_CURRENT_USER);
        userDetailsSetter = setUserDetails;
    } else if (type === 'masq::access-token::set') {
        if (!isInitial && hasValidTokenString) {
            // Only store valid tokens to prevent corrupting storage
            yield storeMasqToken(token);
        }
        userDetailsSetter = setMasqUserDetails;
    } else if (type === 'refresh-token::set') {
        if (!isInitial && hasValidTokenString) {
            // Only store valid tokens to prevent corrupting storage
            yield storeRefreshToken(token);
        }
        return; // there's no user details for refresh tokens, so we can stop here
    } else {
        throw new Error('Unexpected token type');
    }

    if (hasValidTokenString) {
        const decoded = decodeToken(token.token);

        let isSuperuser: boolean;
        if (decoded.realm_access?.roles?.includes('superuser')) {
            isSuperuser = true;
        } else if (decoded.isSuperuser) {
            log.warn({
                message: `Token for ${decoded.userId} has isSuperuser claim but no superuser role in realm_access. This is a legacy token format.`,
            });
            isSuperuser = true;
        } else {
            isSuperuser = false;
        }

        let isDeveloper: boolean;
        if (decoded.realm_access?.roles?.includes('developer')) {
            isDeveloper = true;
        } else if (decoded.isDeveloper) {
            log.warn({
                message: `Token for ${decoded.userId} has isDeveloper claim but no developer role in realm_access. This is a legacy token format.`,
            });
            isDeveloper = true;
        } else {
            isDeveloper = false;
        }

        const userDetails: UserDetails = {
            id: decoded.userId,
            name: decoded.name,
            email: decoded.email ?? '',
            firstName: decoded.given_name ?? '',
            lastName: decoded.family_name ?? '',
            isSuperuser,
            isDeveloper,
            isTestUser: decoded.isTestUser === true,
        };

        if (TRACE) {
            log.trace({ message: 'Got user details', userDetails });
        }

        yield put(userDetailsSetter(userDetails));

        // TODO: This call is only used for checking for a 401 to detect logout. The response body is not used.
        try {
            const configService: SagaContext['configService'] = yield getContext('configService');
            const userInfoURL = configService.oAuthConfig.userInfoURL;

            yield call(axios.get, userInfoURL, {
                headers: {
                    Authorization: `Bearer ${token.token}`,
                },
            });
        } catch (e) {
            // Check if the user's token is not valid
            if (e instanceof AxiosError && e.status === 401) {
                log.info({ message: 'User is not authenticated' });

                if (TRACE) {
                    log.trace({ message: 'Starting terminal logout' });
                }

                // The session is already invalid, so this terminal lifecycle
                // cannot be denied by a navigation guard.
                yield call(onLogoutEvent);
                return;
            } else if (e instanceof Error) {
                log.error({ message: 'Error authenticating user', error: e });
            } else {
                log.fatal({ message: 'Unknown exception', details: e });
            }
        }
    } else {
        // put user undefined
        yield put(userDetailsSetter(undefined));
    }
}

export function* killMasqOnTimeout({ token }: SetTokenAction) {
    if (TRACE) {
        log.trace({ message: 'killMasqOnTimeout', token });
    }

    if (!token) return;

    log.debug({ message: 'Using masquerade access token', token });

    const exp = getExpirationDate(token.token);
    if (!exp) return;

    const {
        cleared,
        replaced,
    }: { delayed: true | undefined; cleared: ClearTokenAction | undefined; replaced: SetTokenAction | undefined } =
        yield race({
            delayed: delayUntil(exp),
            replaced: take('masq::access-token::set'),
            cleared: take('masq::clear'),
        });
    if (cleared || replaced) {
        // no need to renew if it was cleared
        // also, no need for THIS timeout if changed, a new saga will be started
        return;
    }

    yield put(clearMasq('Masquerade session expired'));
}

export function* clearAccessTokenSaga({ reason }: ClearTokenAction) {
    log.info({ message: 'Clearing access token: ', reason });
    yield call(clearStoredAccessToken);
}

export function* clearRefreshTokenSaga({ reason }: ClearTokenAction) {
    log.info({ message: 'Clearing refresh token: ', reason });
    yield call(clearStoredRefreshToken);
}

export function* clearMasqTokenSaga({ reason }: ClearTokenAction) {
    log.info({ message: 'Clearing masquerade access token: ', reason });
    yield call(clearStoredMasqToken);
}

export function* onLogoutEvent() {
    try {
        const loginScreenService: SagaContext['loginScreenService'] = yield getContext('loginScreenService');
        if (!APP_ELECTRON) {
            // May be undefined in SDK
            loginScreenService?.showLoggedOutScreen();
        }

        // TODO: This may not be necessary anymore, as we log out via redirect
        yield all([
            put(clearAccessToken('loggedOut')),
            put(clearRefreshToken('loggedOut')),
            put(clearMasq('loggedOut')),
        ]);

        const oauth2: SagaContext['oAuth2Client'] = yield getContext('oAuth2Client');
        yield call(() => oauth2.logout());

        if (APP_ELECTRON) {
            // May be undefined in SDK
            loginScreenService?.showLoggedOutScreen();
        }
    } catch (e) {
        if (e instanceof Error) {
            log.error({ message: e.message, error: e });
        } else {
            log.fatal({ message: 'Unknown exception', details: e });
        }
    }
}

export function* authSagas() {
    yield all([
        takeLatest<SetTokenAction['type']>('access-token::set', setAccessTokenSaga),
        takeLatest<SetTokenAction['type']>('refresh-token::set', setAccessTokenSaga),
        takeLatest<SetTokenAction['type']>('masq::access-token::set', setAccessTokenSaga),

        takeLatest<SetTokenAction['type']>('masq::access-token::set', killMasqOnTimeout),

        takeLatest<ClearTokenAction['type']>('access-token::clear', clearAccessTokenSaga),
        takeLatest<ClearTokenAction['type']>('refresh-token::clear', clearRefreshTokenSaga),
        takeLatest<ClearTokenAction['type']>('masq::clear', clearMasqTokenSaga),

        // Watch for auth events from managed OAuth service
        spawn(watchAuthEvents),
    ]);
}
